import { useState } from "react";
import type { PricingRule } from "./rates.model";
import { ruleFromPrompt } from "./rates.model";

type RatesPricingProps = {
  rules: PricingRule[];
  onAdd: (rule: PricingRule) => void;
  onRemove: (id: string) => void;
};

export const RatesPricing = ({ rules, onAdd, onRemove }: RatesPricingProps) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState<string | null>(null);

  const send = () => {
    const value = draft.trim();
    if (!value) return;
    setPending(value);
    setDraft("");
    window.setTimeout(() => {
      onAdd(ruleFromPrompt(value));
      setPending(null);
      setOpen(false);
    }, 450);
  };

  return (
    <div className="rt-pane">
      <div className="pra pra--first">
        <div className="pra__hd">
          <div>
            <div className="pcx__h">FCL pricing rules by AI</div>
            <p className="pcx__sub">
              Describe the rule in your own words — the assistant asks only for what is missing and builds the rule
              card.
            </p>
          </div>
          <button type="button" className="pcbtn pcbtn--main" onClick={() => setOpen(true)}>
            + Create Rule with AI
          </button>
        </div>

        {open && (
          <div className="pcx pra__panel">
            <div className="pra__top">
              <div>
                <div className="pcx__h">Create Rule with AI</div>
                <p className="pcx__sub">Sea → FCL only in this version.</p>
              </div>
              <button type="button" className="pcbtn" onClick={() => setOpen(false)}>
                Close
              </button>
            </div>
            <div className="pra__th">
              <div className="pra-m">
                <span className="pra-m__av">QS</span>
                <div className="pra-m__b">
                  Describe the markup in your own words — for example{" "}
                  <b>Add $200 to all 40HQ rates from China to UAE.</b>
                </div>
              </div>
              {pending !== null && (
                <div className="pra-m pra-m--me">
                  <span className="pra-m__av pra-m__av--me">
                    <i />
                  </span>
                  <div className="pra-m__b">{pending}</div>
                </div>
              )}
            </div>
            <div className="pra__comp">
              <textarea
                rows={1}
                value={draft}
                placeholder="Add $200 to all 40HQ rates from China to UAE."
                aria-label="Describe the rule"
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    send();
                  }
                }}
              />
              <button type="button" className="pcbtn pcbtn--main" onClick={send}>
                Send
              </button>
            </div>
          </div>
        )}

        <div className="pra__list">
          {rules.map((rule) => (
            <article key={rule.id} className="prc">
              <div className="prc__hd">
                <span className="prc__kind">FCL</span>
                <span className="prc__lane">{rule.title}</span>
                <span className="prc__st">Active</span>
              </div>
              <div className="prc__g">
                <div className="prc__f">
                  <span>Origin</span>
                  <b>{rule.origin}</b>
                </div>
                <div className="prc__f">
                  <span>Destination</span>
                  <b>{rule.destination}</b>
                </div>
                <div className="prc__f">
                  <span>Equipment</span>
                  <b>{rule.equipment}</b>
                </div>
                <div className="prc__f">
                  <span>Action</span>
                  <b className="is-up">{rule.action}</b>
                </div>
              </div>
              <div className="prc__a">
                <button type="button" className="pcbtn" onClick={() => onRemove(rule.id)}>
                  Delete
                </button>
              </div>
            </article>
          ))}
        </div>
        {rules.length === 0 && !open && (
          <p className="pra__none">No pricing rules yet. Create one with AI — Sea FCL only in this version.</p>
        )}
      </div>
    </div>
  );
};
